import {
  EActionInputType,
  EFormSource,
  ICRUDFormDiMetadata,
  IProcessStepScreensInfo,
  IWorkFlow,
  IWorkFlowDto
} from "@kuika/kuika-cl-model";
import { AxiosResponse } from "axios";
import classNames from "classnames";
import React from "react";
import { FormService } from "../../../services/form-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import ArrowRightSvg from "../../../svgs/small-icons/arrow-right.svg";
import { KModal } from "../../ui-kit/k-modal/k-modal";
import { FormWizardCustom, ICustomSourceOptions } from "./form-wizard-custom";
import { FormWizardLanding } from "./form-wizard-landing";

interface IFormWizardProps {
  /*
   * Form metadata to be edited.
   *
   * If this is provided, the wizard will be in edit mode.
   */
  metadata?: ICRUDFormDiMetadata;
}

interface IFormWizardState {
  page: "landing" | EFormSource;
  formStatus: "draft" | "update";
  isExcelUploadInProgress: boolean;
  isCreatingFormFromDataTableInProgress: boolean;
  isCreatingFormFromActionInProgress: boolean;
  isCreatingFormFromWfInProgress: boolean;
  customSourceOptions: ICustomSourceOptions | null;
  /** The step in charge publishes its own `KModalFooter`. */
  footer: React.ReactNode;
}

class FormWizard extends React.Component<IFormWizardProps & IWithSDProps, IFormWizardState> {
  static initialState: IFormWizardState = {
    page: "landing",
    formStatus: "draft",
    isExcelUploadInProgress: false,
    isCreatingFormFromDataTableInProgress: false,
    isCreatingFormFromActionInProgress: false,
    isCreatingFormFromWfInProgress: false,
    customSourceOptions: null,
    footer: null
  };

  constructor(props: IFormWizardProps & IWithSDProps) {
    super(props);
    this.state = { ...FormWizard.initialState };
  }

  componentDidMount() {
    if (this.props.sd.processIdForWizardForBusinessObject) {
      this.initializeFormWizardForBusinessObject();
      return;
    }

    // For now
    if (
      this.props.metadata?.source === EFormSource.Custom ||
      this.props.metadata?.source === EFormSource.DataTable ||
      this.props.metadata?.source === EFormSource.Excel ||
      this.props.metadata?.source === EFormSource.Action ||
      this.props.metadata?.source === EFormSource.WorkflowBo
    ) {
      this.setState({ page: EFormSource.Custom, formStatus: "update" });
    }
  }

  private setFooter = (footer: React.ReactNode) => {
    this.setState({ footer });
  };

  private initializeFormWizardForBusinessObject = async () => {
    try {
      this.setState({ isCreatingFormFromWfInProgress: true });
      if (!this.props.sd.processIdForWizardForBusinessObject) {
        return;
      }

      const processStepScreen = this.props.sd.project.processStepScreenIds?.find(
        (p) => p.screenId === this.props.sd.sdId
      );

      if (!processStepScreen) {
        throw new Error("Process step screen cannot be found");
      }

      const metadataResponse = await FormService.GetFormMetadataFromWorkflowBo({
        processId: processStepScreen.processId,
        stepId: processStepScreen.stepId
      });

      if (metadataResponse.status !== 200) {
        throw new Error("Error while getting metadata");
      }

      this.props.sd.setMetadataForFormWizard(metadataResponse.data);
      this.setState({ page: EFormSource.WorkflowBo, formStatus: "draft" });
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
    } finally {
      this.setState({ isCreatingFormFromWfInProgress: false });
    }
  };

  private onClose = () => {
    this.setState({ ...FormWizard.initialState });
    this.props.sd.setIsWizardForFormOpen(false);
    this.props.sd.setTargetKuikaIdForFormWizard(null);
    this.props.sd.setProcessIdForWizardForBusinessObject(null);
    this.props.sd.setMetadataForFormWizard(null);
  };

  private getModalHeader = (): React.ReactNode => {
    const page = this.props.metadata?.source ?? this.state.page;

    if (page === EFormSource.Custom) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div>{this.state.formStatus === "update" ? "Update form" : "Create form"}</div>
          <ArrowRightSvg />
          <div
            data-testid="wcustomText"
            data-onboarding-id="common_form-wizard_custom-text"
            style={{ color: "var(--dark-6)" }}
          >
            Custom
          </div>
        </div>
      );
    }

    if (page === EFormSource.AI) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div>{this.state.formStatus === "update" ? "Update form" : "Create form"}</div>
          <ArrowRightSvg />
          <div
            data-testid="withAIText"
            data-onboarding-id="common_form-wizard_with-ai-text"
            style={{ color: "var(--dark-6)" }}
          >
            With AI
          </div>
        </div>
      );
    }

    if (page === EFormSource.WorkflowBo) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div>{this.state.formStatus === "update" ? "Update form" : "Create form"}</div>
          <ArrowRightSvg />
          <div
            data-testid="withProcessAutomationText"
            data-onboarding-id="common_form-wizard_with-process-automation-text"
            style={{ color: "var(--dark-6)" }}
          >
            With Process Automation
          </div>
        </div>
      );
    }

    if (page === EFormSource.DataTable) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div>{this.state.formStatus === "update" ? "Update form" : "Create form"}</div>
          <ArrowRightSvg />
          <div
            data-testid="withDataTableText"
            data-onboarding-id="common_form-wizard_with-datatable-text"
            style={{ color: "var(--dark-6)" }}
          >
            With data table
          </div>
        </div>
      );
    }

    if (page === EFormSource.Excel) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div>{this.state.formStatus === "update" ? "Update form" : "Create form"}</div>
          <ArrowRightSvg />
          <div
            data-testid="withExcelText"
            data-onboarding-id="common_form-wizard_with-excel-text"
            style={{ color: "var(--dark-6)" }}
          >
            With Excel
          </div>
        </div>
      );
    }

    if (page === EFormSource.Action) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div>{this.state.formStatus === "update" ? "Update form" : "Create form"}</div>
          <ArrowRightSvg />
          <div
            data-testid="withActionText"
            data-onboarding-id="common_form-wizard_with-action-text"
            style={{ color: "var(--dark-6)" }}
          >
            With action
          </div>
        </div>
      );
    }

    return "Create form";
  };

  private onClickNextCustom = (alsoCreateTable: boolean, nameOfTheTableToBeCreated: string) => {
    this.setState({
      page: EFormSource.Custom,
      formStatus: "draft",
      customSourceOptions: alsoCreateTable ? { tableName: nameOfTheTableToBeCreated } : null
    });
  };

  // TODO: Maybe make this a util function?
  private readFile = (file: File): Promise<string | ArrayBuffer | null> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  private onClickNextExcel = async (file: File) => {
    try {
      this.setState({ isExcelUploadInProgress: true });
      if (!file) {
        throw new Error("File is not uploaded");
      }

      let fileContent = await this.readFile(file);

      if (!fileContent) {
        throw new Error("File content is empty");
      }

      if (fileContent instanceof ArrayBuffer) {
        fileContent = Buffer.from(fileContent).toString("base64");
      }

      const response = await FormService.GetFormMetadataFromExcel({
        base64Str: fileContent
      });

      if (response.status !== 200) {
        throw new Error("Error while parsing excel file");
      }

      const metadata = response.data;
      this.props.sd.setMetadataForFormWizard(metadata, () => {
        this.setState({ page: EFormSource.Excel, formStatus: "draft" });
      });
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
    } finally {
      this.setState({ isExcelUploadInProgress: false });
    }
  };

  private onClickNextDataTable = async (dataSourceId: string, tableName: string) => {
    try {
      this.setState({ isCreatingFormFromDataTableInProgress: true });

      const response = await FormService.GetFormMetadataFromDataTable({
        dataSourceId,
        tableName
      });

      if (response.status !== 200) {
        throw new Error("Error while parsing data table");
      }

      const metadata = response.data;
      this.props.sd.setMetadataForFormWizard(metadata, () => {
        this.setState({ page: EFormSource.DataTable, formStatus: "draft" });
      });
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
    } finally {
      this.setState({ isCreatingFormFromDataTableInProgress: false });
    }
  };

  private onClickNextAI = () => {};

  private onClickNextWf = async (processId: string, stepId: string) => {
    try {
      this.setState({ isCreatingFormFromWfInProgress: true });

      const response = await FormService.GetFormMetadataFromWorkflowBo({
        processId,
        stepId
      });

      if (response.status !== 200) {
        throw new Error("Error while parsing data table");
      }

      const metadata = response.data;
      this.props.sd.setMetadataForFormWizard(metadata, () => {
        this.setState({ page: EFormSource.WorkflowBo, formStatus: "draft" });
      });
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
    } finally {
      this.setState({ isCreatingFormFromWfInProgress: false });
    }
  };

  private getWf = (processStepScreen: IProcessStepScreensInfo): IWorkFlow | undefined => {
    let wf: IWorkFlow | undefined;

    if (processStepScreen.projectId && processStepScreen.projectId !== "") {
      const projectWithWf = this.props.sd.projectsWithWFData.find(
        (project) => project.id === processStepScreen?.projectId
      );

      const workflows = projectWithWf?.workflows as IWorkFlowDto[] | undefined;

      wf = workflows?.find((wf) => wf.id === processStepScreen?.processId);
    } else {
      wf = this.props.sd.workFlowList.find((wf) => wf.id === processStepScreen?.processId);
    }

    return wf;
  };

  private getMetadataFromAction = async (
    actionDefinitionId: string,
    actionInputType: EActionInputType | undefined,
    selectedProcess: string | undefined
  ) => {
    const actionDefinition = this.props.sd.actionDefinitions?.find((a) => a.id === actionDefinitionId);

    if (!actionDefinition) {
      throw new Error("Action definition not found");
    }

    let response: AxiosResponse<ICRUDFormDiMetadata>;

    if (actionDefinition.name === "StartProcess") {
      if (!selectedProcess) {
        throw new Error("Selected process is required");
      }

      response = await FormService.GetFormMetadataForStartProcess({
        processId: selectedProcess
      });
    } else {
      if (actionInputType === undefined) {
        throw new Error("Action input type is required");
      }

      response = await FormService.GetFormMetadataFromAction({
        actionDefinitionId,
        actionInputType
      });
    }

    return response;
  };

  private onClickNextAction = async (
    actionDefinitionId: string,
    actionInputType: EActionInputType | undefined,
    selectedProcess: string | undefined
  ) => {
    try {
      this.setState({ isCreatingFormFromActionInProgress: true });

      const response = await this.getMetadataFromAction(actionDefinitionId, actionInputType, selectedProcess);

      if (response.status !== 200) {
        throw new Error("Error while parsing data table");
      }

      const metadata = response.data;
      this.props.sd.setMetadataForFormWizard(metadata, () => {
        this.setState({ page: EFormSource.Action, formStatus: "draft" });
      });
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
    } finally {
      this.setState({ isCreatingFormFromActionInProgress: false });
    }
  };

  render() {
    return (
      <KModal
        // The source step is four cards and a picker; only the editor needs the tall sheet.
        className={classNames("form-wizard-container", {
          "form-wizard-container--landing": this.state.page === "landing"
        })}
        showModal
        header={this.getModalHeader()}
        closeModal={this.onClose}
        width={960}
        isLoading={
          this.state.isCreatingFormFromDataTableInProgress ||
          this.state.isExcelUploadInProgress ||
          this.state.isCreatingFormFromActionInProgress ||
          this.state.isCreatingFormFromWfInProgress
        }
        footer={this.state.footer}
      >
        {this.state.page === "landing" && (
          <FormWizardLanding
            isExcelUploadInProgress={this.state.isExcelUploadInProgress}
            isCreatingFormFromDataTableInProgress={this.state.isCreatingFormFromDataTableInProgress}
            isCreatingFormFromActionInProgress={this.state.isCreatingFormFromActionInProgress}
            isCreatingFormFromWfInProgress={this.state.isCreatingFormFromWfInProgress}
            onClickNextCustom={this.onClickNextCustom}
            onClickNextDataTable={this.onClickNextDataTable}
            onClickNextExcel={this.onClickNextExcel}
            onClickNextWf={this.onClickNextWf}
            onClickNextAI={this.onClickNextAI}
            onClickNextAction={this.onClickNextAction}
            onCancel={this.onClose}
            setFooter={this.setFooter}
          />
        )}
        {this.state.page !== "landing" && (
          <FormWizardCustom
            onCancel={this.onClose}
            mode="default"
            formStatus={this.state.formStatus}
            customSourceOptions={this.state.customSourceOptions}
            metadata={this.props.metadata}
            closeModal={this.onClose}
            setFooter={this.setFooter}
          />
        )}
      </KModal>
    );
  }
}

const formWizard = withSD<IFormWizardProps>(FormWizard);
export { formWizard as FormWizard };
